/**
Copyright (C) 2019-2022, Infraclear Inc. All Rights Reserved.
*/


import React, { useEffect, useState } from "react";
import "./changePassword.scss";
import { associateGoogleTOTP, forcePassChange } from "../../constants/api-urlConstants";
import { ErrorCodes } from "../../constants/messageCodes";
import { MessageText } from "../../constants/messageText";
import { accessMangerBasePath, passwordLength } from "../../constants/env-url";
import {
  Button,
  TextField,
  IconButton,
  CircularProgress,
} from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import instance from "../../utils/axiosInstance";
import { SnackBarMessage } from "../snackBar/snackBar";
import { SetBarcode } from "../setBarcode/setBarcode";
import { PasswordFormat } from "../passwordFormat/passwordFormat";
import { encryptpassword } from "../../constants/commonConstants";
import { whiteSpaceRestrict } from "../../constants/commonConstants";

export const ChangePassword: React.FC<any> = ({ goToLoginPopup, errorObjectData }) => {
  const [qrCode, setQrCode] = useState<any>("");
  const [showActivate, setShowActivate] = React.useState(false);
  const [showQrcode, setShowQrcode] = useState<any>(false);
  const [msgConfig, setMsgConfig] = useState({
    type: '',
    message: '',
    snackFlag: false
  })
  const [credentialsObj, setCredentials] = useState<any>();
  const [showPassword, setShowPassword] = useState<any>(false);
  const [showNewPassword, setShowNewPassword] = useState<any>(false);
  const [showCnfPassword, setShowCnfPassword] = useState<any>(false);
  const [statusIsLoading, setStatusIsLoading] = React.useState(false);
  const [barcodeLoading, setBarcodeLoading] = React.useState(false);
  const [passwordTooltip, setPasswordTooltip] = useState<any>(false);
  const [passwordTextBoxValue, setPasswordTextBoxValue] = useState<any>();
  const [initial_state, setInitial_state] = useState<any>({
    email: "",
    password: "",
    newPassword: "",
    confirmpassword: ""
  });

  const regex = new RegExp(`^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{${passwordLength},}$`);

  const ChangePasswordValidation = Yup.object().shape({
    email: Yup.string().email(MessageText.VALIDATION_EMAIL_FORMAT).required(MessageText.VALIDATION_EMAIL_REQUIRED),
    password: Yup.string().required(MessageText.VALIDATION_OLD_PASS_REQUIRED),
    newPassword: Yup.string().required(MessageText.VALIDATION_PASS_REQUIRED).matches(
      regex,
      MessageText.VALIDATION_PASS_INVALID
    ),
    confirmpassword: Yup.string().required(MessageText.VALIDATION_CONFIRM_PASS_REQUIRED)
      .oneOf([Yup.ref('newPassword')], MessageText.VALIDATION_PASS_MATCH)

  });


  useEffect(() => {
    if (errorObjectData.error === ErrorCodes.MFA_SETUP && errorObjectData.credentials) {
      setCredentials(errorObjectData.credentials);
      setBarcodeLoading(true);
      generateMFA(errorObjectData.credentials);
    } else if (errorObjectData.error === ErrorCodes.NEW_PASSWORD_REQUIRED && errorObjectData.credentials) {
      setInitial_state(
        {
          email: errorObjectData.credentials.email,
          password: errorObjectData.credentials.password,
          newPassword: "",
          confirmpassword: ""
        }
      )
    }
  }, []);

  const onFinish = (credentials: any) => {
    if (credentials) {
      setStatusIsLoading(true);
      resetPassword(credentials)
    }
  };

  const goToLogin = (value: any = false) => {
    const obj: any = {
      showMsg: value.showMsg,
      hideChangepass: false,
    }
    if (value?.MFA_EXPIRED) {
      obj['MFA_EXPIRED'] = value.MFA_EXPIRED
    }
    goToLoginPopup(obj)
  }

  const resetPassword = async (credentials: any) => {
    const encrypt_password: any = await encryptpassword(credentials.password);
    const encrypt_newPassword: any = await encryptpassword(credentials.newPassword);
    const payload = {
      "email": credentials.email,
      "password": encrypt_password,
      "newPassword": encrypt_newPassword
    }
    const USER_API_BASE_URL = `${forcePassChange}`;
    instance
      .post(USER_API_BASE_URL, payload)
      .then((response) => {
        const res = response.data;
        if (!res.success) {
          setCredentials(credentials);
          if (res.message_code === ErrorCodes.MFA_SETUP) {
            generateMFA(credentials, true)
          }
          else if (res?.message_code === ErrorCodes.INVALID_USERNAME_PASSWORD) {
            setStatusIsLoading(false);
            setMsgConfig({
              type: 'error',
              message: MessageText.USERNAME_PASS_NO_MATCH,
              snackFlag: true
            });
          }
        } else if (res.success) {
          setMsgConfig({
            type: 'success',
            message: MessageText.PASSWORD_CHANGE_SUCC,
            snackFlag: true
          });
          window.location.href = `${accessMangerBasePath}`;
        } else {
          setStatusIsLoading(false);
        }
      })
      .catch((error) => {
        setStatusIsLoading(false);
        setMsgConfig({
          type: 'error',
          message: MessageText.USERNAME_PASS_NO_MATCH,
          snackFlag: true
        });
      });

  }

  const generateMFA = async (credentials: any, fromChangePass: any = false) => {
    const encrypt_password: any = await encryptpassword(credentials.password);
    const encrypt_newPassword: any = await encryptpassword(credentials.newPassword);
    const MFApayload = {
      "email": credentials.email,
      "password": fromChangePass ? encrypt_newPassword : encrypt_password,
    }

    const USER_API_BASE_URL = `${associateGoogleTOTP}`;
    instance
      .post(USER_API_BASE_URL, MFApayload)
      .then((response) => {
        const res = response.data
        setBarcodeLoading(false);
        setStatusIsLoading(false);
        if (res.success) {
          setQrCode(res.data);
          setShowActivate(res.data.validated);
          errorObjectData.error = "MFA_SETUP";
          setShowQrcode(true);
        }
      })
      .catch((error) => {
        setStatusIsLoading(false);
      });
  }

  return (
    <div className="changePassword">
      {errorObjectData.error === ErrorCodes.NEW_PASSWORD_REQUIRED &&
        <div className="changePasswordWrapper">
          <span className="changePasswordtitle">Change password</span>
          <Formik
            enableReinitialize={true}
            initialValues={initial_state}
            validationSchema={ChangePasswordValidation}
            onSubmit={onFinish}
          >
            {({ touched, errors, values, handleBlur, handleChange, handleSubmit, setFieldValue }) => (
              <Form onSubmit={handleSubmit} style={{ marginTop: "10px" }}>
                <Field
                  as={TextField}
                  margin="normal"
                  error={touched.email && Boolean(errors.email)}
                  fullWidth
                  label="Email*"
                  name="email"
                  autoComplete="email"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    autoComplete: 'new-email',
                    form: {
                      autoComplete: 'off',
                    },
                  }}
                  helperText={<ErrorMessage name="email" />}
                />
                <Field
                  as={TextField}
                  type={showPassword ? "text" : "password"}
                  margin="normal"
                  className="inputField"
                  fullWidth
                  name="password"
                  label="Temporary Password*"
                  id="password"
                  size="small"
                  error={touched.password && Boolean(errors.password)}
                  helperText={<ErrorMessage name="password" />}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton tabIndex={-1}
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <Visibility />
                          ) : (
                            <VisibilityOff />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                    autoComplete: 'new-password',
                    form: {
                      autoComplete: 'off',
                    },
                  }}
                />
                <div className='newPassword'>
                  {
                    passwordTooltip && <PasswordFormat textBoxValue={passwordTextBoxValue}
                      allTrue={(value: any) => {
                      }} />
                  }
                  <Field
                    as={TextField}
                    type={showNewPassword ? "text" : "password"}
                    margin="normal"
                    className="inputField"
                    fullWidth
                    name="newPassword"
                    label="New Password*"
                    id="newPassword"
                    onKeyPress={(e: any) => whiteSpaceRestrict(e)}
                    onFocus={(event: any) => {
                      setPasswordTextBoxValue(event.target.value)
                      setPasswordTooltip(true)

                    }}
                    onBlur={(event: any) => {

                      setPasswordTooltip(false);
                      handleBlur(event)
                    }}
                    onChange={(event: any) => {
                      setFieldValue("newPassword", event.target.value);
                      setPasswordTextBoxValue(event.target.value);

                    }}
                    InputLabelProps={{ shrink: true }}
                    error={touched.newPassword && Boolean(errors.newPassword)}
                    helperText={<ErrorMessage name="newPassword" />}
                    size="small"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton tabIndex={-1}
                            aria-label="toggle password visibility"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                          >
                            {showNewPassword ? (
                              <Visibility />
                            ) : (
                              <VisibilityOff />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
                <Field
                  as={TextField}

                  margin="normal"
                  className="inputField"
                  fullWidth
                  name="confirmpassword"
                  label="Confirm New Password*"
                  type={showCnfPassword ? "text" : "password"}
                  id="confirmpassword"
                  size="small"
                  onKeyPress={(e: any) => whiteSpaceRestrict(e)}
                  InputLabelProps={{ shrink: true }}
                  error={touched.confirmpassword && Boolean(errors.confirmpassword)}
                  helperText={<ErrorMessage name="confirmpassword" />}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton tabIndex={-1}
                          aria-label="toggle password visibility"
                          onClick={() => setShowCnfPassword(!showCnfPassword)}
                        >
                          {showCnfPassword ? (
                            <Visibility />
                          ) : (
                            <VisibilityOff />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <div className="loader">
                  {statusIsLoading && <div className="progressCircle">
                    <CircularProgress />
                  </div>}
                  {
                    !statusIsLoading &&
                    <Button
                      type="submit"
                      variant="contained"
                      className="submitBtn"
                      style={{ textTransform: "none" }}
                    >
                      Change Password
                    </Button>
                  }
                </div>
              </Form>
            )}
          </Formik>
        </div>
      }
      {barcodeLoading && <div className="waitTillBarcode">
        <CircularProgress />
      </div>
      }
      {errorObjectData.error === ErrorCodes.MFA_SETUP && showQrcode && !barcodeLoading &&
        <SetBarcode qrCodeData={qrCode} credentialsObjData={credentialsObj}
          showActivateForm={showActivate}
          goToPrevious={(value: any) => {
            goToLogin(value);
          }} />
      }
      {msgConfig.snackFlag && <div>
        <SnackBarMessage snacBarValue={msgConfig}
        />
      </div>}
    </div>
  );
};
